import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Input } from '../design-system';
import { InterpolatableInput } from '../flows/InterpolatableInput';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useLocale } from '../../contexts/LocaleContext';
import { colors, spacing, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { DataListWebhookFieldMappingMode } from '../../services/api';
import { FieldMatcherProps, useFieldMappingState } from './field-matcher-types';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { withAlpha } from '../../theme';

/**
 * The field matcher — a plain scrollable list of target columns, each with a
 * mode switch (Field/Fixed/Expression), a searchable dropdown of detected
 * source fields for Field mode, and InterpolatableInput's insert-value
 * helper for Expression mode. Used on every platform (a prior ReactFlow
 * drag-connect canvas existed for web only; it was replaced because dragging
 * a line between two columns was fiddly and error-prone, especially with the
 * 10-15+ columns a system module like Products can have — clicking a field
 * from a list is faster and harder to get wrong at any column count).
 */
export const FieldMatcher: React.FC<FieldMatcherProps> = ({ sourceFields, targetColumns, value, onChange, showExternalId, showOrderRelationPickers, showProductSkuPicker }) => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  const { t } = useLocale();
  const {
    getMapping, setFieldPath, setMode, setFixedValue, setExpression,
    setExternalId, setProductSkuField, setContactIdField, setContactIdentifierField,
  } = useFieldMappingState(value, onChange);

  const [openPickerFor, setOpenPickerFor] = useState<string | null>(null);
  const [pickerFilter, setPickerFilter] = useState('');
  const [manualEntryFor, setManualEntryFor] = useState<Record<string, boolean>>({});

  const variableGroups = useMemo(
    () => [{ key: 'webhook-fields', label: t('dataLists.schema.webhook.sourceFieldsTitle'), entries: sourceFields.map((f) => ({ path: f, label: f })) }],
    [sourceFields, t]
  );

  const filteredFields = (columnId: string) => {
    const filter = openPickerFor === columnId ? pickerFilter.trim().toLowerCase() : '';
    return filter ? sourceFields.filter((f) => f.toLowerCase().includes(filter)) : sourceFields;
  };

  const isMapped = (mapping: ReturnType<typeof getMapping>): boolean => {
    if (!mapping) return false;
    if (mapping.mode === 'fixed') return !!mapping.fixedValue;
    if (mapping.mode === 'expression') return !!mapping.expression;
    return !!mapping.sourcePath; // field mode (default)
  };

  // Repeater columns (e.g. Orders' "items") can never be populated by a
  // single flat field mapping — they need either a dedicated mechanism
  // (Items Mapping, rendered separately for Orders) or manual per-record
  // entry. Listing one here as an ordinary mappable target duplicated Items
  // Mapping on screen for no benefit (the mapping could never actually work).
  const mappableColumns = targetColumns.filter((col) => col.type !== 'repeater');

  return (
    <View>
      {mappableColumns.map((col) => {
        const mapping = getMapping(col.id);
        const mode: DataListWebhookFieldMappingMode = mapping?.mode || 'field';
        const pickerOpen = openPickerFor === col.id;
        const mapped = isMapped(mapping);
        const showManualEntry = mode === 'field' && (manualEntryFor[col.id] || (!!mapping?.sourcePath && !sourceFields.includes(mapping.sourcePath)));

        return (
          <View key={col.id} style={styles.card}>
            <View style={[styles.cardHeader, { flexDirection: rtl.rowDirection }]}>
              <Ionicons
                name={mapped ? 'checkmark-circle' : 'ellipse-outline'}
                size={16}
                color={mapped ? colors.success : colors.textLight}
              />
              <Text style={[styles.columnName, { textAlign: rtl.textStart }]}>{col.name}</Text>
            </View>

            <View style={[styles.modeRow, { flexDirection: rtl.rowDirection }]}>
              {(['field', 'fixed', 'expression'] as const).map((m) => (
                <TouchableOpacity key={m} style={[styles.modePill, mode === m && styles.modePillActive]} onPress={() => setMode(col.id, m)}>
                  <Text style={[styles.modePillText, mode === m && styles.modePillTextActive]}>{t(`dataLists.schema.webhook.mode.${m}` as any)}</Text>
                </TouchableOpacity>
              ))}
            </View>

            {mode === 'field' && (
              <>
                <View style={[styles.selectRow, { flexDirection: rtl.rowDirection }]}>
                  <TouchableOpacity
                    style={[styles.selectButton, { flexDirection: rtl.rowDirection }]}
                    onPress={() => {
                      setOpenPickerFor(pickerOpen ? null : col.id);
                      setPickerFilter('');
                    }}
                  >
                    <Text style={[styles.selectButtonText, !mapping?.sourcePath && styles.selectButtonPlaceholder, { textAlign: rtl.textStart }]} numberOfLines={1}>
                      {mapping?.sourcePath || t('dataLists.schema.webhook.selectFieldPlaceholder')}
                    </Text>
                    <Ionicons name="chevron-down" size={18} color={colors.textLight} />
                  </TouchableOpacity>
                  {!!mapping?.sourcePath && (
                    <TouchableOpacity
                      style={styles.clearButton}
                      onPress={() => setFieldPath(col.id, '')}
                      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    >
                      <Ionicons name="close-circle" size={18} color={colors.textLight} />
                    </TouchableOpacity>
                  )}
                </View>
                {pickerOpen && (
                  <View style={styles.inlineOptions}>
                    <TextInput
                      value={pickerFilter}
                      onChangeText={setPickerFilter}
                      placeholder={t('dataLists.schema.webhook.selectFieldPlaceholder')}
                      placeholderTextColor={colors.textLight as string}
                      style={[styles.filterInput, { textAlign: rtl.textStart }]}
                      autoCapitalize="none"
                    />
                    {filteredFields(col.id).length === 0 ? (
                      <Text style={[styles.hint, { padding: spacing.md, textAlign: rtl.textStart }]}>{t('dataLists.schema.webhook.noFieldsDetected')}</Text>
                    ) : (
                      <ScrollView style={styles.inlineOptionsScroll} nestedScrollEnabled keyboardShouldPersistTaps="handled">
                        {filteredFields(col.id).map((f) => (
                          <TouchableOpacity
                            key={f}
                            style={styles.inlineOption}
                            onPress={() => {
                              setFieldPath(col.id, f);
                              setOpenPickerFor(null);
                            }}
                          >
                            <Text style={[styles.inlineOptionText, mapping?.sourcePath === f && styles.inlineOptionTextActive]}>{f}</Text>
                          </TouchableOpacity>
                        ))}
                      </ScrollView>
                    )}
                  </View>
                )}

                {showManualEntry ? (
                  <Input
                    value={mapping?.sourcePath || ''}
                    onChangeText={(v) => setFieldPath(col.id, v)}
                    placeholder={t('dataLists.schema.webhook.sourcePathPlaceholder')}
                    autoCapitalize="none"
                    containerStyle={{ marginTop: spacing.xs }}
                  />
                ) : (
                  <TouchableOpacity onPress={() => setManualEntryFor((prev) => ({ ...prev, [col.id]: true }))} style={{ marginTop: spacing.xs }}>
                    <Text style={[styles.manualEntryLink, { textAlign: rtl.textStart }]}>{t('dataLists.schema.webhook.typeManually')}</Text>
                  </TouchableOpacity>
                )}

                {((showExternalId && mapping?.sourcePath) || (showProductSkuPicker && mapping?.sourcePath) || (showOrderRelationPickers && mapping?.sourcePath)) && (
                  <View style={[styles.flagPillRow, { flexDirection: rtl.rowDirection }]}>
                    {showExternalId && mapping?.sourcePath && (
                      <TouchableOpacity style={[styles.modePill, mapping?.isExternalId && styles.modePillActive]} onPress={() => setExternalId(col.id, !mapping?.isExternalId)}>
                        <Text style={[styles.modePillText, mapping?.isExternalId && styles.modePillTextActive]}>{t('dataLists.schema.webhook.externalId')}</Text>
                      </TouchableOpacity>
                    )}
                    {showProductSkuPicker && mapping?.sourcePath && (
                      <TouchableOpacity style={[styles.modePill, mapping?.isProductSku && styles.modePillActive]} onPress={() => setProductSkuField(col.id, !mapping?.isProductSku)}>
                        <Text style={[styles.modePillText, mapping?.isProductSku && styles.modePillTextActive]}>{t('dataLists.schema.webhook.productSkuField')}</Text>
                      </TouchableOpacity>
                    )}
                    {showOrderRelationPickers && mapping?.sourcePath && (
                      <>
                        <TouchableOpacity style={[styles.modePill, mapping?.isContactId && styles.modePillActive]} onPress={() => setContactIdField(col.id, !mapping?.isContactId)}>
                          <Text style={[styles.modePillText, mapping?.isContactId && styles.modePillTextActive]}>{t('dataLists.schema.webhook.contactIdField')}</Text>
                        </TouchableOpacity>
                        <TouchableOpacity style={[styles.modePill, mapping?.isContactIdentifier && styles.modePillActive]} onPress={() => setContactIdentifierField(col.id, !mapping?.isContactIdentifier)}>
                          <Text style={[styles.modePillText, mapping?.isContactIdentifier && styles.modePillTextActive]}>{t('dataLists.schema.webhook.contactIdentifierField')}</Text>
                        </TouchableOpacity>
                      </>
                    )}
                  </View>
                )}
              </>
            )}

            {mode === 'fixed' && (
              <Input
                value={mapping?.fixedValue || ''}
                onChangeText={(v) => setFixedValue(col.id, v)}
                placeholder={t('dataLists.schema.webhook.fixedValuePlaceholder')}
              />
            )}

            {mode === 'expression' && (
              <>
                <InterpolatableInput
                  variables={variableGroups}
                  value={mapping?.expression || ''}
                  onChangeText={(v) => setExpression(col.id, v)}
                  placeholder={t('dataLists.schema.webhook.expressionPlaceholder')}
                  autoCapitalize="none"
                />
                <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{t('dataLists.schema.webhook.expressionHint')}</Text>
              </>
            )}
          </View>
        );
      })}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  card: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    padding: spacing.md,
    marginBottom: spacing.md,
    ...designTokens.shadows.sm,
  },
  columnName: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  modeRow: { gap: spacing.xs, marginBottom: spacing.sm },
  modePill: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs / 1.5,
    borderRadius: designTokens.borderRadius.full ?? 999,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  modePillActive: { backgroundColor: withAlpha(colors.primary, 0.08), borderColor: colors.primary },
  modePillText: { fontSize: typography.fontSize.xs, color: colors.text.secondary, fontFamily: typography.fontFamily.medium },
  modePillTextActive: { color: colors.primary, fontFamily: typography.fontFamily.semibold },
  flagPillRow: { gap: spacing.xs, marginTop: spacing.xs },
  cardHeader: { alignItems: 'center', gap: spacing.xs, marginBottom: spacing.xs },
  selectRow: { alignItems: 'center', gap: spacing.xs },
  selectButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.background.surface,
  },
  selectButtonText: { flex: 1, fontSize: typography.fontSize.base, color: colors.text.primary, fontFamily: typography.fontFamily.regular },
  selectButtonPlaceholder: { color: colors.textLight },
  clearButton: { padding: spacing.xs },
  manualEntryLink: { fontSize: typography.fontSize.xs, color: colors.primary, fontFamily: typography.fontFamily.medium },
  inlineOptions: {
    marginTop: spacing.xs,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.background.surface,
    overflow: 'hidden',
  },
  inlineOptionsScroll: {
    maxHeight: 220,
  },
  filterInput: {
    padding: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    fontFamily: typography.fontFamily.regular,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  inlineOption: { padding: spacing.md, borderBottomWidth: 1, borderBottomColor: colors.border },
  inlineOptionText: { fontSize: typography.fontSize.base, color: colors.text.primary, fontFamily: typography.fontFamily.regular },
  inlineOptionTextActive: { color: colors.primary, fontFamily: typography.fontFamily.semibold },
  hint: { fontSize: typography.fontSize.xs, color: colors.textLight, fontFamily: typography.fontFamily.regular, marginTop: spacing.xs / 2 },
});
